import React, { useEffect, useState, useCallback } from "react";
import { Plus, Bike, Search, Pencil, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { COUNTRIES } from "@/lib/country-context";
import adminApi from "@/lib/admin-api";

const VEHICLES = [
  { v: "motorcycle", label: "Motorcycle" },
  { v: "scooter", label: "Scooter" },
  { v: "bicycle", label: "Bicycle" },
  { v: "car", label: "Car" },
  { v: "foot", label: "On foot" },
];

const STATUS_STYLE = {
  available: "bg-green-100 text-green-700 border-green-200",
  busy: "bg-amber-100 text-amber-700 border-amber-200",
  offline: "bg-muted text-muted-foreground border",
  suspended: "bg-red-100 text-red-700 border-red-200",
};

export default function AdminRidersPage() {
  const { toast } = useToast();
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | rider

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await adminApi.get("/riders", { params });
      setRiders(data);
    } catch {
      toast({ title: "Couldn't load riders", variant: "destructive" });
    } finally { setLoading(false); }
  }, [search, toast]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (r) => {
    if (!window.confirm(`Delete rider ${r.full_name}?`)) return;
    try {
      await adminApi.delete(`/riders/${r.id}`);
      toast({ title: "Rider removed" });
      load();
    } catch (e) {
      toast({ title: "Couldn't delete", description: e?.response?.data?.detail, variant: "destructive" });
    }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "suspended" ? "available" : "suspended";
    try {
      await adminApi.patch(`/riders/${r.id}`, { status: next, is_active: next !== "suspended" });
      toast({ title: next === "suspended" ? "Rider suspended" : "Rider reactivated" });
      load();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-bold">Riders</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Add, manage and monitor your delivery fleet.</p>
        </div>
        <Button className="gap-2" onClick={() => setEditing("new")} data-testid="admin-add-rider">
          <Plus className="w-4 h-4" /> Add rider
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by name or phone..." className="pl-9 h-10"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-riders-search" />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : riders.length === 0 ? (
        <div className="bg-card border rounded-2xl p-12 text-center text-muted-foreground">
          <Bike className="w-12 h-12 mx-auto opacity-20 mb-3" />
          <p className="font-medium">No riders yet — add your first one.</p>
        </div>
      ) : (
        <div className="bg-card border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3 font-semibold">Name</th>
                  <th className="text-left p-3 font-semibold">Phone</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">Vehicle</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold hidden md:table-cell">Deliveries</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {riders.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`admin-rider-row-${r.id}`}>
                    <td className="p-3 font-medium">{r.full_name}</td>
                    <td className="p-3 text-muted-foreground">{r.phone}</td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell capitalize">{r.vehicle_type}</td>
                    <td className="p-3">
                      <Badge className={`text-[10px] font-semibold border ${STATUS_STYLE[r.status]}`}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right text-muted-foreground hidden md:table-cell">{r.total_deliveries}</td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(r)}
                          className="text-xs h-8" data-testid={`admin-rider-toggle-${r.id}`}>
                          {r.status === "suspended" ? "Activate" : "Suspend"}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(r)}
                          data-testid={`admin-rider-edit-${r.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(r)}
                          data-testid={`admin-rider-delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <RiderDialog
          rider={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RiderDialog({ rider, onClose, onSaved }) {
  const { toast } = useToast();
  const isNew = !rider;
  const initialCountry = rider?.country || "GH";
  const initialDial = COUNTRIES[initialCountry]?.dialCode || "+233";

  // For editing existing rider with full international phone, split into prefix and local
  const splitPhone = (p, dial) => {
    if (!p) return "";
    if (p.startsWith(dial)) return p.slice(dial.length);
    // Try other dialcodes too
    for (const c of Object.values(COUNTRIES)) {
      if (p.startsWith(c.dialCode)) return p.slice(c.dialCode.length);
    }
    return p.replace(/^\+?/, "");
  };

  const [form, setForm] = useState({
    full_name: rider?.full_name || "",
    country: initialCountry,
    phone_local: splitPhone(rider?.phone, initialDial),
    vehicle_type: rider?.vehicle_type || "motorcycle",
    id_number: rider?.id_number || "",
    notes: rider?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dial = COUNTRIES[form.country].dialCode;

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone_local.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    const fullPhone = `${dial}${form.phone_local.replace(/\D/g, "").replace(/^0+/, "")}`;
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name, phone: fullPhone, country: form.country,
        vehicle_type: form.vehicle_type, id_number: form.id_number || null,
        notes: form.notes || null,
      };
      if (isNew) {
        await adminApi.post("/riders", payload);
        toast({ title: "Rider added" });
      } else {
        await adminApi.patch(`/riders/${rider.id}`, payload);
        toast({ title: "Rider updated" });
      }
      onSaved();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{isNew ? "Add rider" : "Edit rider"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Full name</Label>
            <Input required value={form.full_name} onChange={upd("full_name")}
              data-testid="admin-rider-name" />
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <div className="grid grid-cols-2 gap-2">
              {["GH", "NG"].map((code) => {
                const c = COUNTRIES[code];
                const active = form.country === code;
                return (
                  <button key={code} type="button" onClick={() => setForm({ ...form, country: code })}
                    data-testid={`admin-rider-country-${code}`}
                    className={`flex items-center gap-2 p-2 rounded-lg border-2 text-sm text-left ${
                      active ? "border-primary bg-primary/5" : "border-input"
                    }`}>
                    <span className="text-lg">{c.flag}</span>
                    <span className="font-medium">{c.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{c.dialCode}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background overflow-hidden">
              <div className="flex items-center px-3 bg-muted text-muted-foreground text-sm font-semibold border-r">
                {dial}
              </div>
              <input type="tel" inputMode="numeric" required value={form.phone_local}
                onChange={(e) => setForm({ ...form, phone_local: e.target.value.replace(/\D/g, "") })}
                className="flex-1 bg-transparent px-3 text-sm focus:outline-none"
                placeholder={form.country === "GH" ? "24 123 4567" : "801 234 5678"}
                data-testid="admin-rider-phone" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Vehicle</Label>
            <select value={form.vehicle_type} onChange={upd("vehicle_type")}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="admin-rider-vehicle">
              {VEHICLES.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>ID number (optional)</Label>
            <Input value={form.id_number} onChange={upd("id_number")} placeholder="National ID / license"
              data-testid="admin-rider-id" />
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea value={form.notes} onChange={upd("notes")} rows={2}
              placeholder="Internal notes about this rider"
              data-testid="admin-rider-notes" />
          </div>
          <Button type="submit" disabled={saving} className="w-full font-semibold" data-testid="admin-rider-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isNew ? "Add rider" : "Save changes")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
